/** Same-origin JSON client for apps/customer-api, plus the shapes the area reads. */

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message?: string, readonly data?: unknown) {
    super(message ?? code);
  }
}

export type Lang = "pt" | "en" | "es" | "fr" | "de";

export interface PublicBrand {
  code: string;
  name: string;
  shortName: string;
  logoUrl: string;
  theme: { primary: string; primaryText: string; accent: string; surface: string; font: string };
  channels: { email: string; whatsapp: string; phone: string };
  legalEntity: string;
  groupStatement: Record<string, string>;
  loyaltyProgramme: string;
  markets: string[];
  defaultMarket: string;
}

export interface BrandInfo {
  brand: PublicBrand | null;
  brands: { code: string; name: string; origin: string }[];
  /** On a test system (CUSTOMER_STAGING_BANNER): whether channels are simulated or logged, and the outbox at `staffUrl`. Null in production. */
  staging: { simulatedChannels: boolean; staffUrl: string } | null;
  /** The social sign-ins configured for the customers (Google, Apple). */
  signIn?: { google: boolean; apple: boolean };
}

export interface LoyaltyBalance { currency: string; points: number; value?: number }
export interface Loyalty {
  balances?: LoyaltyBalance[];
  tier?: string | { name?: string } | null;
  next?: unknown;
  expiresAt?: string | null;
}

export interface Me {
  identity: { id: string; email: string; phone: string | null; name: string | null; language: string; preferences: Record<string, unknown>; createdAt: string };
  session: { kind: "full" | "link"; scope: { kind: string; subjectId: string } | null };
  brand: PublicBrand;
  contact: { id: string; firstName?: string; lastName?: string; consultantId?: string | null } | null;
  consultant: { id: string; name: string; languages: string[] } | null;
  loyalty: Loyalty | null;
  consents: { brand: string; purpose: string; given: boolean }[];
}

export type TripStatus = "proposal" | "pending" | "confirmed" | "in_progress" | "completed" | "cancelled";

export interface TripService { id: string; kind: string; status: string; title: string; sell?: { amountMinor: number; currency: string } }

export interface TripSummary {
  id: string;
  reference: string;
  brand: string;
  source: "platform" | "legacy" | "proposal" | "demo" | string;
  /** Payments, travellers, companions and changes in the area; false only for the seeded demo history. */
  managed?: boolean;
  title: string;
  destination: string | null;
  status: TripStatus;
  startsOn: string | null;
  endsOn: string | null;
  timezone: string;
  language: string;
  currency: string;
  totalMinor: number;
  travellers: number;
  holderName: string | null;
  data: { services?: TripService[]; paymentPlan?: unknown; orderStatus?: string; origin?: string; legacy?: { bookingRef: string; status: string } } & Record<string, unknown>;
}

export type TimelineKind = "flight" | "accommodation" | "transfer" | "activity" | "experience" | "rail" | "cruise" | "car" | "note";

export interface TimelineItem {
  id: string;
  kind: TimelineKind | string;
  title: string;
  startsAt: string | null;
  endsAt: string | null;
  tz: string;
  status: string;
  documentUrl: string | null;
  payload: Record<string, unknown>;
}

export interface TripMember { id: string; name: string | null; email: string | null; role: "holder" | "companion" | string; joinedAt: string | null; invitedAt?: string | null; travellerSlot?: number | null }

export interface TripDetail { trip: TripSummary; timeline: TimelineItem[]; members: TripMember[]; role: "holder" | "companion"; documents?: TripDocument[]; reviews?: ReviewInvitation[] }

/** A hotel review the traveller is invited to write after the stay (the form is the platform's). */
export interface ReviewInvitation { propertyId: string; propertyName: string; checkIn: string; checkOut: string; expiresAt: string; url: string }

export interface StartResult { challengeId: string; channel: "email" | "sms" | "whatsapp"; to: string; expiresAt: string; codeLength?: number }
export interface VerifyResult { created: boolean; me: Me }

export interface OwnMessage { id: string; channel: string; kind: string; subject: string | null; body: string; link: string | null; createdAt: string; status: string }

export interface LinkOpen { kind: "trip" | "proposal" | "payment" | "help" | "sign_in" | "residents"; subjectId: string; brand: string; email: string | null; hasIdentity: boolean }

export interface StaffMessage {
  id: string; identityId: string | null; tripId: string | null; brand: string; channel: string; to: string; kind: string; language: string;
  subject: string | null; body: string; link: string | null; code: string | null; status: string; provider: string; createdAt: string; readAt: string | null;
}
export interface StaffIdentity { id: string; email: string; phone: string | null; name: string | null; language: string; createdAt: string; lastSignInAt: string | null; crmContactId: string | null }
export interface StaffEvent { seq: number; type: string; aggregate: string; aggregateId: string; payload: Record<string, unknown>; createdAt: string }

// ── C2: proposals, payments and traveller data ────────────────────────────────

export type ProposalStatus = "sent" | "viewed" | "option_chosen" | "accepted" | "declined" | "expired";
export type ProposalItemKind = "flight" | "accommodation" | "transfer" | "activity" | "experience" | "rail" | "cruise" | "other";

export interface ProposalItem { kind: ProposalItemKind | string; title: string; detail?: string; day?: number; start?: string; end?: string; endDay?: number; payload?: Record<string, unknown> }
export interface ProposalOption { id: string; title: string; description?: string; recommended?: boolean; priceMinor: number; items: ProposalItem[] }
export interface ProposalStoryDay { day: number; title: string; text: string }
export interface ProposalConsultant { id?: string; name: string; photoUrl?: string; bio?: string }
export interface PaymentTerms { depositPercent?: number; depositMinor?: number; balanceDaysBefore?: number }

export interface Proposal {
  id: string;
  brand: string;
  language: string;
  holderEmail: string;
  holderName: string | null;
  title: string;
  destination: string | null;
  summary: string | null;
  coverUrl: string | null;
  currency: string;
  travellers: number;
  startsOn: string | null;
  endsOn: string | null;
  timezone: string;
  status: ProposalStatus;
  options: ProposalOption[];
  chosenOptionId: string | null;
  story: ProposalStoryDay[];
  consultant: ProposalConsultant | null;
  paymentTerms: PaymentTerms;
  validUntil: string | null;
  tripId: string | null;
  createdAt: string;
  sentAt: string | null;
  viewedAt: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
  /** Only on the customer view. */
  pointsToEarn?: number;
  loyaltyProgramme?: string;
}

export interface ProposalQuestion { id: string; proposalId?: string; text: string; answer: string | null; askedAt: string; answeredAt: string | null; answeredBy: string | null }

/** The pre-contract information form of an option (Directive (EU) 2015/2302): required for packages before acceptance. */
export type PreContract = { required: false; optionId: string } | { required: true; optionId: string; html: string; sha256: string; accepted: { id: string; version: number; at: string } | null };
/** A document of the trip: kept by the area (the accepted pre-contract form) or linked from the booking (legacy). */
export interface TripDocument { id?: string; kind: string; name: string; url?: string; version?: number; acceptedAt?: string | null; language?: string }
export interface ProposalView { proposal: Proposal; questions: ProposalQuestion[]; trip: TripSummary | null }

export interface ProposalListItem {
  id: string; title: string; destination: string | null; status: ProposalStatus; startsOn: string | null; endsOn: string | null; brand: string; currency: string; validUntil: string | null; tripId: string | null; sentAt: string | null;
}

export type PaymentMethod = "card" | "mbway" | "transfer" | "apple_pay" | "google_pay";
export type PaymentKind = "deposit" | "balance" | "instalment" | "refund";
export type PaymentStatus = "due" | "pending" | "paid" | "failed" | "refunded" | "cancelled";
export type PlanStatus = "deposit_due" | "deposit_paid" | "balance_due" | "paid" | "overdue" | "refunded";
export type SimulateOutcome = "paid" | "failed" | "pending";

export interface Instalment { n: number; dueOn: string; amountMinor: number }
export interface InstalmentOption { months: number; monthly: number; firstOn: string; lastOn: string }

export interface PaymentPlan {
  id: string; currency: string; totalMinor: number; depositMinor: number; balanceMinor: number; balanceDueOn: string; instalments: Instalment[] | null;
  pointsApplied: number; pointsValueMinor: number; status: PlanStatus; instalmentOptions: InstalmentOption[];
}
export interface Payment {
  id: string; kind: PaymentKind; instalmentNo: number | null; amountMinor: number; currency: string; method: string | null; status: PaymentStatus; dueOn: string | null; settledAt: string | null; failureReason: string | null;
}
export interface PaymentTotals { paid: number; refunded: number; due: number; outstanding: number; currency: string }
export interface NextPayment { id: string; kind: PaymentKind; instalmentNo: number | null; amountMinor: number; dueOn: string | null }
export interface PaymentsView { plan: PaymentPlan; payments: Payment[]; totals: PaymentTotals; next: NextPayment | null }
/** `redirectUrl`: the gateway's hosted payment page (Stripe Checkout, Worldline); the customer comes back on the trip's link. */
export interface PayResult { payment: Payment; plan: PaymentPlan; totals: PaymentTotals; redirectUrl?: string }

export type TravellerStatus = "missing" | "submitted" | "validated" | "rejected";
export interface Traveller {
  id: string; slot: number; givenNames: string | null; surname: string | null; dateOfBirth: string | null; nationality: string | null; documentType: string | null; documentNumber: string | null;
  documentExpiry: string | null; gender: string | null; dueOn: string | null; submittedAt: string | null; validatedAt: string | null; rejectedReason: string | null; status: TravellerStatus;
}
export interface TravellersView { travellers: Traveller[]; editableSlots: number[]; dueOn: string | null }
export interface TravellerInput { givenNames: string; surname: string; dateOfBirth: string; nationality: string; documentType: "passport" | "id_card"; documentNumber: string; documentExpiry: string; gender?: "F" | "M" | "X" }

export interface StaffTripDetail { trip: TripSummary; timeline: TimelineItem[]; members: (TripMember & { travellerSlot?: number | null })[]; travellers: Traveller[]; payments: PaymentsView | null }

// ── C3: conversation, assistant and requests ──────────────────────────────────

export type ThreadChannel = "email" | "whatsapp" | "call" | "area" | "assistant";
export type ReplyVia = "email" | "whatsapp" | "area";
export type ThreadAuthor = "customer" | "consultant" | "assistant" | "system";
export type ThreadKind = "message" | "change_request" | "cancellation_request" | "question" | "handoff";

export interface ThreadMessage {
  id: string;
  channel: ThreadChannel | string;
  direction: "in" | "out";
  author: ThreadAuthor | string;
  authorName: string | null;
  body: string;
  subject: string | null;
  kind: ThreadKind | string;
  at: string;
  readAt: string | null;
  tripId: string | null;
  /** Only on the staff listing, when the API sends it. */
  identityId?: string | null;
}

export interface Conversation { messages: ThreadMessage[]; replyVia: ReplyVia; assistant: "copilot" | "simulated" | string }
export interface SendResult { message: ThreadMessage; assistant: ThreadMessage | null; handedOver: boolean }
export interface AssistantResult { question: ThreadMessage | null; answer: ThreadMessage | null; handedOver: boolean; provider: string }
export type RequestKind = "change" | "cancellation";

// ── C4: trip companion ────────────────────────────────────────────────────────

/** On a flight item after a `flight.schedule_changed` event: the previous times, when, and the Flights app's note. */
export interface ScheduleChange { from: { startsAt: string | null; endsAt: string | null }; at: string; note: string | null }
/** On a transfer item after a driver event. */
export interface Driver { name: string; vehicle: string | null; plate: string | null; phone: string | null; liveUrl: string | null; status: "assigned" | "en_route" | string; at: string }

export type NoticeChannel = "push" | "whatsapp" | "email";
export const NOTICE_CHANNELS: NoticeChannel[] = ["push", "whatsapp", "email"];
export interface NoticePreferences { before?: boolean; during?: boolean; after?: boolean; channels?: NoticeChannel[] }
export interface PushKey { publicKey: string | null; simulated: boolean }

export interface NoticeRule {
  key: string;
  trigger: "departure" | "return" | "day" | "event" | string;
  offsetMinutes: number;
  localTime: string | null;
  channelPolicy: NoticeChannel[];
  template: string;
  audience: "holder" | "members" | string;
  enabled: boolean;
  brand?: string | null;
  language?: string | null;
}
export interface NoticeLogEntry { id?: string; tripId: string; ruleKey: string; occurrence: string; recipientEmail: string; scheduledFor: string; sentAt: string | null; channel: string | null; status: string }
export interface NoticeSequenceEntry { ruleKey: string; occurrence: string; at: string; localDay: string; items: string[] }
export interface TripNotices { sequence: NoticeSequenceEntry[]; log: NoticeLogEntry[] }
export interface RunTripNotices extends TripNotices { planned: number; sent: number }
export type OrderEventKind = "documents_issued" | "changed" | "cancelled";

// ── C5: Getaways Club and Help now ──────────────────────────────────────────

/** The programme as `GET /api/v1/loyalty` shows it (services/loyalty.ts LoyaltyView). */
export interface LoyaltyTier { code: string; name: string; threshold: number; benefits: string[] }
export interface LoyaltyView {
  programme: string;
  balances: { currency: string; points: number; valueMinor: number }[];
  tier: { code: string; name: string; benefits: string[] } | null;
  next: { code: string; name: string; progress: number; benefits: string[] } | null;
  expiresAt: string | null;
  earnRate: number;
  redeemRate: number;
  tiers: LoyaltyTier[];
}
export interface PointsValue { points: number; valueMinor: number }
export interface TripLoyalty { loyalty: LoyaltyView | null; redeemable: PointsValue; applied: PointsValue | null }
export interface RedeemResult { redeemed: PointsValue; payments: PaymentsView | null }

export type Urgency = "urgent" | "soon" | "info";
export const URGENCIES: Urgency[] = ["urgent", "soon", "info"];
export interface EmergencyCase {
  id: string; tripId: string; what: string; where: string | null; callbackPhone: string | null; urgency: Urgency | string; status: "open" | "closed" | string;
  assignedAgentName: string | null; assignedVia: string | null; assistantReply: string | null; openedAt: string; closedAt: string | null; note: string | null;
}
export interface HelpInput { what: string; where?: string; callbackPhone?: string; canCall?: boolean; urgency?: Urgency }
export interface HelpResult { emergency: EmergencyCase; assistantReply: string | null; line: string }

export interface StaffEmergency extends EmergencyCase { trip: TripSummary | null }
export interface StaffAgent { id: string; name: string; email?: string }
export interface OnCallEntry { day: string; brand: string; agentId: string; backupAgentId: string | null; agent: StaffAgent | null; backup: StaffAgent | null }
export interface OnCallView { entries: OnCallEntry[]; agents: StaffAgent[]; crm: boolean }
/** The CRM's programme record; only what the staff page reads is typed. */
export interface LoyaltyProgramme { name?: string; tiers?: { code: string; name: string; threshold?: { default?: number }; benefits?: string[] }[]; earnPointsPerUnit?: number; redeemPointsPerUnit?: number; expiryMonths?: number }

// ── C6: launch ────────────────────────────────────────────────────────────────

/** The privacy notice as `GET /api/v1/privacy` returns it; `brand` is the brand code of the host, or null on the API host. */
export interface PrivacyNotice { version: string; language: Lang; languages: Lang[]; controller: string; brand: string | null; title: string; sections: { heading: string; text: string }[] }

export interface Monitoring {
  since: string;
  days: number;
  notices: { ruleKey: string; channel: string | null; status: string; sent: number; total: number }[];
  outbox: { channel: string; status: string; provider: string; total: number }[];
  events: { type: string; total: number }[];
  assistant: { answers: number; handovers: number };
  emergencies: { status: string; urgency: string; total: number }[];
  signIns: number;
  identities: number;
  providers: { identity: string; channels: string; payments: string; assistant: string; push: string; email: string; whatsapp: string; sms: string; signIn?: { code: boolean; google: boolean; apple: boolean } };
  pending: { stripe: boolean; email: boolean; whatsapp: boolean; webPush: boolean; sms: boolean; customersZitadel: boolean; googleApple: boolean; stagingBanner: boolean; demoHistory: boolean; legalReview: boolean };
  /** Per channel, the settings it still lacks to go live. */
  missing?: Record<"email" | "whatsapp" | "push" | "sms" | "signIn", string[]>;
}
export type MonitoringDays = 7 | 30 | 90;

// ── C7: the customer's data (docs/16 §6) ──────────────────────────────────────

/** `GET /api/v1/me/export` as a file: the JSON body and the name from content-disposition. */
export interface DataExport { blob: Blob; filename: string }
export interface DeletionRequested { requested: boolean; confirmBy: "email" | string }
export interface DeletionResult { identityId: string; erased: boolean; brand?: string }
export const MONITORING_DAYS: MonitoringDays[] = [7, 30, 90];

export const STAFF_KEY_STORAGE = "staffKey";

// ── Staff sign-in through Resosys Access (central) or the staff key (local) ──

export type AuthConfig = { mode: "central"; loginUrl: string; accountUrl: string; portalUrl?: string } | { mode: "local" };
export interface StaffMe { user: { id: string; email: string; name?: string | null } & Record<string, unknown>; organizations: unknown[] }
export interface AccessApp { key: string; name: string; url: string; current: boolean }

async function request<T>(method: string, path: string, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  if (body !== undefined) headers["content-type"] = "application/json";
  const res = await fetch(path, { method, headers, credentials: "same-origin", body: body === undefined ? undefined : JSON.stringify(body) });
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? `http_${res.status}`, err.message, data);
  }
  return data as T;
}

const EXPORT_FALLBACK_NAME = "my-trips.json";

/** The filename of a content-disposition header (`filename*=UTF-8''…` first, then `filename="…"`), or the fallback. */
export function filenameOf(disposition: string | null, fallback = EXPORT_FALLBACK_NAME): string {
  if (!disposition) return fallback;
  const star = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/.exec(disposition);
  return plain?.[1]?.trim() || fallback;
}

/** The export is not JSON to parse but a file to save: same-origin fetch, the body as a blob. */
async function download(path: string): Promise<DataExport> {
  const res = await fetch(path, { method: "GET", headers: { accept: "application/json" }, credentials: "same-origin" });
  if (!res.ok) {
    let data: unknown = undefined;
    try {
      data = await res.json();
    } catch {
      data = undefined;
    }
    const err = (data ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? `http_${res.status}`, err.message, data);
  }
  return { blob: await res.blob(), filename: filenameOf(res.headers.get("content-disposition")) };
}

export const api = {
  brand: () => request<BrandInfo>("GET", "/api/v1/brand"),
  me: () => request<Me>("GET", "/api/v1/me"),
  authStart: (body: { email?: string; phone?: string; language: string; via?: "sms" | "whatsapp" }) => request<StartResult>("POST", "/api/v1/auth/start", body),
  authVerify: (body: { challengeId: string; code: string }) => request<VerifyResult>("POST", "/api/v1/auth/verify", body),
  authMagic: (token: string) => request<VerifyResult>("POST", "/api/v1/auth/magic", { token }),
  logout: () => request<{ ok: boolean }>("POST", "/api/v1/auth/logout"),
  patchMe: (body: { name?: string; language?: string; phone?: string; preferences?: Record<string, unknown> }) => request<Me>("PATCH", "/api/v1/me", body),
  setConsent: (body: { brand: string; purpose: "service" | "marketing" | "profiling"; given: boolean }) => request<{ consents: Me["consents"] }>("PUT", "/api/v1/me/consents", body),
  messages: () => request<{ messages: OwnMessage[] }>("GET", "/api/v1/me/messages"),
  trips: () => request<{ trips: TripSummary[] }>("GET", "/api/v1/trips"),
  trip: (id: string) => request<TripDetail>("GET", `/api/v1/trips/${encodeURIComponent(id)}`),
  openLink: (token: string) => request<LinkOpen>("POST", `/api/v1/links/${encodeURIComponent(token)}/open`),
  // C2
  proposals: () => request<{ proposals: ProposalListItem[] }>("GET", "/api/v1/proposals"),
  proposal: (id: string) => request<ProposalView>("GET", `/api/v1/proposals/${encodeURIComponent(id)}`),
  chooseOption: (id: string, optionId: string) => request<{ proposal: Proposal }>("POST", `/api/v1/proposals/${encodeURIComponent(id)}/choose`, { optionId }),
  askQuestion: (id: string, text: string) => request<{ question: ProposalQuestion }>("POST", `/api/v1/proposals/${encodeURIComponent(id)}/questions`, { text }),
  preContract: (id: string, optionId: string | undefined) => request<PreContract>("GET", `/api/v1/proposals/${encodeURIComponent(id)}/pre-contract${optionId ? `?optionId=${encodeURIComponent(optionId)}` : ""}`),
  acceptPreContract: (id: string, optionId: string | undefined, sha256: string) => request<{ document: TripDocument | null }>("POST", `/api/v1/proposals/${encodeURIComponent(id)}/pre-contract/accept`, { sha256, ...(optionId ? { optionId } : {}) }),
  acceptProposal: (id: string, optionId: string | undefined) =>
    request<{ proposal: Proposal; trip: TripSummary; payments: PaymentsView | null }>("POST", `/api/v1/proposals/${encodeURIComponent(id)}/accept`, optionId ? { optionId } : {}),
  declineProposal: (id: string, reason?: string) => request<{ proposal: Proposal }>("POST", `/api/v1/proposals/${encodeURIComponent(id)}/decline`, reason ? { reason } : {}),
  payments: (tripId: string) => request<{ payments: PaymentsView | null }>("GET", `/api/v1/trips/${encodeURIComponent(tripId)}/payments`),
  pay: (tripId: string, paymentId: string, body: { method: PaymentMethod; simulate?: SimulateOutcome; phone?: string }) =>
    request<{ result: PayResult; payments: PaymentsView | null }>("POST", `/api/v1/trips/${encodeURIComponent(tripId)}/payments/${encodeURIComponent(paymentId)}/pay`, body),
  chooseInstalments: (tripId: string, months: number) => request<{ payments: PaymentsView | null }>("POST", `/api/v1/trips/${encodeURIComponent(tripId)}/payments/instalments`, { months }),
  travellers: (tripId: string) => request<TravellersView>("GET", `/api/v1/trips/${encodeURIComponent(tripId)}/travellers`),
  putTraveller: (tripId: string, slot: number, body: TravellerInput) => request<{ traveller: Traveller }>("PUT", `/api/v1/trips/${encodeURIComponent(tripId)}/travellers/${slot}`, body),
  // C3
  conversation: (options: { markRead?: boolean } = {}) => request<Conversation>("GET", `/api/v1/conversation${options.markRead === false ? "?markRead=false" : ""}`),
  sendMessage: (body: { text: string; replyVia?: ReplyVia }) => request<SendResult>("POST", "/api/v1/conversation/messages", body),
  askAssistant: (text: string) => request<AssistantResult>("POST", "/api/v1/assistant/messages", { text }),
  tripRequest: (tripId: string, body: { kind: RequestKind; text: string }) => request<{ message: ThreadMessage }>("POST", `/api/v1/trips/${encodeURIComponent(tripId)}/requests`, body),
  // C4
  inviteMember: (tripId: string, body: { email: string; name?: string }) => request<{ member: TripMember }>("POST", `/api/v1/trips/${encodeURIComponent(tripId)}/members`, body),
  removeMember: (tripId: string, memberId: string) => request<{ ok: boolean }>("DELETE", `/api/v1/trips/${encodeURIComponent(tripId)}/members/${encodeURIComponent(memberId)}`),
  putNotices: (body: NoticePreferences) => request<{ notices: NoticePreferences | undefined }>("PUT", "/api/v1/me/notices", body),
  pushKey: () => request<PushKey>("GET", "/api/v1/push/key"),
  pushSubscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) => request<{ ok: boolean; subscriptions: number }>("POST", "/api/v1/push/subscribe", body),
  // C5
  loyalty: (language?: string) => request<{ loyalty: LoyaltyView | null }>("GET", `/api/v1/loyalty${language ? `?language=${encodeURIComponent(language)}` : ""}`),
  tripLoyalty: (tripId: string) => request<TripLoyalty>("GET", `/api/v1/trips/${encodeURIComponent(tripId)}/loyalty`),
  redeemPoints: (tripId: string, points: number) => request<RedeemResult>("POST", `/api/v1/trips/${encodeURIComponent(tripId)}/loyalty/redeem`, { points }),
  help: (tripId: string, body: HelpInput) => request<HelpResult>("POST", `/api/v1/trips/${encodeURIComponent(tripId)}/help`, body),
  helpCases: (tripId: string) => request<{ cases: EmergencyCase[] }>("GET", `/api/v1/trips/${encodeURIComponent(tripId)}/help`),
  // C6
  privacy: (language: Lang) => request<PrivacyNotice>("GET", `/api/v1/privacy?language=${encodeURIComponent(language)}`),
  // C7
  exportData: () => download("/api/v1/me/export"),
  requestDeletion: () => request<DeletionRequested>("POST", "/api/v1/me/deletion"),
  confirmDeletion: (token: string) => request<DeletionResult>("POST", `/api/v1/me/deletion/confirm/${encodeURIComponent(token)}`),
};

const staffHeaders = (): Record<string, string> => {
  let key: string | null = null;
  try {
    key = sessionStorage.getItem(STAFF_KEY_STORAGE);
  } catch {
    key = null;
  }
  return key ? { "x-staff-key": key } : {};
};

export const staffAuthApi = {
  config: () => request<AuthConfig>("GET", "/auth/config"),
  me: () => request<StaffMe>("GET", "/auth/me"),
  apps: () => request<{ apps: AccessApp[] }>("GET", "/auth/apps"),
  logout: () => request<{ ok: boolean; redirect?: string }>("POST", "/auth/logout"),
};

export const staffApi = {
  status: () => request<Record<string, unknown>>("GET", "/api/staff/status", undefined, staffHeaders()),
  outbox: () => request<{ messages: StaffMessage[] }>("GET", "/api/staff/outbox?limit=200", undefined, staffHeaders()),
  markRead: (id: string) => request<{ ok: boolean }>("POST", `/api/staff/outbox/${encodeURIComponent(id)}/read`, undefined, staffHeaders()),
  identities: () => request<{ identities: StaffIdentity[] }>("GET", "/api/staff/identities", undefined, staffHeaders()),
  trips: () => request<{ trips: TripSummary[] }>("GET", "/api/staff/trips", undefined, staffHeaders()),
  events: () => request<{ events: StaffEvent[] }>("GET", "/api/staff/events", undefined, staffHeaders()),
  // C2
  proposals: () => request<{ proposals: Proposal[]; questions: ProposalQuestion[] }>("GET", "/api/staff/proposals", undefined, staffHeaders()),
  proposal: (id: string) => request<{ proposal: Proposal; questions: ProposalQuestion[]; link: string }>("GET", `/api/staff/proposals/${encodeURIComponent(id)}`, undefined, staffHeaders()),
  publishProposal: (body: Record<string, unknown>) => request<{ proposal: Proposal; link: string }>("POST", "/api/staff/proposals", body, staffHeaders()),
  answerQuestion: (id: string, questionId: string, answer: string, by: string) =>
    request<{ question: ProposalQuestion }>("POST", `/api/staff/proposals/${encodeURIComponent(id)}/questions/${encodeURIComponent(questionId)}/answer`, { answer, by }, staffHeaders()),
  trip: (id: string) => request<StaffTripDetail>("GET", `/api/staff/trips/${encodeURIComponent(id)}`, undefined, staffHeaders()),
  simulatePayment: (tripId: string, paymentId: string, outcome: SimulateOutcome | "refund") =>
    request<Record<string, unknown>>("POST", `/api/staff/trips/${encodeURIComponent(tripId)}/payments/${encodeURIComponent(paymentId)}/simulate`, { outcome }, staffHeaders()),
  createPlan: (tripId: string) => request<{ payments: PaymentsView | null }>("POST", `/api/staff/trips/${encodeURIComponent(tripId)}/payment-plan`, {}, staffHeaders()),
  validateTraveller: (tripId: string, slot: number, by: string, reject?: string) =>
    request<{ traveller: Traveller }>("POST", `/api/staff/trips/${encodeURIComponent(tripId)}/travellers/${slot}/validate`, { by, ...(reject ? { reject } : {}) }, staffHeaders()),
  runJobs: () => request<{ results: Record<string, number | string> }>("POST", "/api/staff/jobs/run", {}, staffHeaders()),
  // C3
  threads: (identityId?: string) => request<{ messages: ThreadMessage[] }>("GET", identityId ? `/api/staff/threads?identityId=${encodeURIComponent(identityId)}` : "/api/staff/threads", undefined, staffHeaders()),
  replyThread: (identityId: string, body: { text: string; by: string; via?: ReplyVia }) =>
    request<{ message: ThreadMessage | null }>("POST", `/api/staff/threads/${encodeURIComponent(identityId)}/reply`, body, staffHeaders()),
  // C4: notices and the live status simulator (the internal event routes take the same staff key)
  noticeRules: () => request<{ rules: NoticeRule[] }>("GET", "/api/staff/notice-rules", undefined, staffHeaders()),
  updateNoticeRule: (key: string, body: { enabled?: boolean; localTime?: string | null }) => request<{ rule: NoticeRule }>("PUT", `/api/staff/notice-rules/${encodeURIComponent(key)}`, body, staffHeaders()),
  noticesLog: () => request<{ log: NoticeLogEntry[] }>("GET", "/api/staff/notices", undefined, staffHeaders()),
  runNotices: () => request<Record<string, number>>("POST", "/api/staff/notices/run", {}, staffHeaders()),
  tripNotices: (tripId: string) => request<TripNotices>("GET", `/api/staff/trips/${encodeURIComponent(tripId)}/notices`, undefined, staffHeaders()),
  runTripNotices: (tripId: string, all: boolean) => request<RunTripNotices>("POST", `/api/staff/trips/${encodeURIComponent(tripId)}/notices/run`, all ? { all: true } : {}, staffHeaders()),
  resendInvite: (tripId: string, memberId: string) => request<{ sent: number }>("POST", `/api/staff/trips/${encodeURIComponent(tripId)}/members/${encodeURIComponent(memberId)}/invite`, {}, staffHeaders()),
  transferDriver: (body: { tripId: string; itemId: string; status: "assigned" | "en_route"; driver: string; vehicle?: string; plate?: string; phone?: string; liveUrl?: string }) =>
    request<{ item: TimelineItem }>("POST", "/api/internal/events/transfer-driver", body, staffHeaders()),
  orderEvent: (body: { tripId: string; kind: OrderEventKind; documentsUrl?: string; change?: string }) => request<{ trip: TripSummary }>("POST", "/api/internal/events/order", body, staffHeaders()),
  // C5: emergencies, the on-call roster and the programme
  emergencies: () => request<{ cases: StaffEmergency[] }>("GET", "/api/staff/emergencies", undefined, staffHeaders()),
  closeEmergency: (id: string, body: { by: string; note?: string }) => request<{ emergency: EmergencyCase }>("POST", `/api/staff/emergencies/${encodeURIComponent(id)}/close`, body, staffHeaders()),
  onCall: (from: string, to: string) => request<OnCallView>("GET", `/api/staff/on-call?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, undefined, staffHeaders()),
  putOnCall: (day: string, body: { brand?: string; agentId: string; backupAgentId?: string | null }) => request<Record<string, unknown>>("PUT", `/api/staff/on-call/${encodeURIComponent(day)}`, body, staffHeaders()),
  loyaltyProgramme: () => request<{ programme: LoyaltyProgramme | null }>("GET", "/api/staff/loyalty/programme", undefined, staffHeaders()),
  ensureProgramme: () => request<{ programme: LoyaltyProgramme | null }>("POST", "/api/staff/loyalty/programme/ensure", {}, staffHeaders()),
  // C6: what the team watches at launch
  monitoring: (days: MonitoringDays) => request<Monitoring>("GET", `/api/staff/monitoring?days=${days}`, undefined, staffHeaders()),
};

// ── Residents (docs/22 §12): the household, its documents and the resident's flights ──

export interface ResidentMember { id: string; self: boolean; givenNames: string; surname: string; dateOfBirth: string | null; nif: string | null; relation: string | null; documentType: string | null; documentNumber: string | null; documentExpiry: string | null; status: "pending" | "verified" | "rejected"; note: string | null }
export interface ResidentDocument { id: string; memberId: string | null; kind: string; mimeType: string; fileName: string | null; expiresOn: string | null; status: "received" | "verified" | "rejected" | "replaced" | "expired"; note: string | null; receivedAt: string; hasFile: boolean }
export interface ResidentTrip {
  id: string;
  code: string;
  orderReference: string;
  status: string;
  airline: string;
  pnr?: string;
  legs: { origin: string; destination: string; departure: string; arrival: string; flights: string[] }[];
  passengers: { name: string; category?: string; tickets: string[]; subsidy: number; share: number; eligible: boolean; reason?: string }[];
  customerPays: number;
  advanceTotal: number;
  subsidyTotal: number;
  total: number;
  refundable: boolean;
  documents: { kind: string; url: string; label: string }[];
  claims: { passenger: string; status: string; amount: number; advanced: number; reference?: string; refusal?: string }[];
  collections: { description: string; amount: number; status: string; url: string }[];
  canChange: boolean;
  canCancel: boolean;
}
export interface ResidentSection {
  household: { members: ResidentMember[]; documents: ResidentDocument[] };
  resident: {
    customer: { status: string; category?: string; missing: string[]; preferredPayment?: string; brandName: string };
    mandate: { status: "signed" | "missing"; signedAt?: string; version?: number; link?: string };
    openAdvance: number;
    trips: ResidentTrip[];
  } | null;
  notices: { id: string; kind: string; text: string; link: string | null; tripCode: string | null; createdAt: string; readAt: string | null }[];
}
export interface ResidentChangeOption { optionKey: string; legs: ResidentTrip["legs"]; airlineCost: number; penalty: number; extraSubsidy: number; customerPays: number }

export const residentsApi = {
  section: () => request<ResidentSection>("GET", "/api/v1/residents"),
  readNotices: () => request<{ ok: boolean }>("POST", "/api/v1/residents/notices/read", {}),
  addMember: (body: Record<string, string>) => request<ResidentMember>("POST", "/api/v1/residents/members", body),
  updateMember: (id: string, body: Record<string, string>) => request<ResidentMember>("PUT", `/api/v1/residents/members/${id}`, body),
  removeMember: (id: string) => request<{ ok: boolean }>("DELETE", `/api/v1/residents/members/${id}`),
  uploadDocument: (body: { memberId?: string; kind: string; mimeType: string; fileName?: string; base64: string }) => request<ResidentDocument>("POST", "/api/v1/residents/documents", body),
  changeOptions: (code: string, journey: number, date: string) => request<{ options: ResidentChangeOption[] }>("GET", `/api/v1/residents/trips/${code}/change-options?journey=${journey}&date=${date}`),
  change: (code: string, body: { journey: number; date: string; optionKey: string }) => request<{ trip: ResidentTrip; paymentUrl?: string }>("POST", `/api/v1/residents/trips/${code}/change`, body),
  cancellationQuote: (code: string) => request<{ ticketed: boolean; cost: number; balance: number; explanation: string }>("GET", `/api/v1/residents/trips/${code}/cancellation`),
  cancel: (code: string) => request<{ balance: number }>("POST", `/api/v1/residents/trips/${code}/cancel`, {}),
};
